/*eslint-disable max-lines*/
import {
  useNeighbourAlarms,
  useSharedNeighborAlarms,
} from '@hakimo-ui/hakimo/data-access';
import { getNeighborAlarmsSearchParams } from '@hakimo-ui/hakimo/feature-shared';
import { NeighbourAlarmsResponse, UnifiedAlarm } from '@hakimo-ui/hakimo/types';
import { BasicPanel, QueryResult } from '@hakimo-ui/hakimo/ui-elements';
import { Table, TableData } from '@hakimo-ui/hakimo/ui-table';
import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

const ONE_MINUTE_MS = 60 * 1000;
const NEIGHBOR_COUNT = 3;
interface Prop {
  event: UnifiedAlarm;
  sharedToken?: string;
}

export function NeighboringEvent({ event, sharedToken }: Prop) {
  const startTimestamp = event.alarmStartTime - ONE_MINUTE_MS;
  const endTimestamp = event.alarmStartTime + ONE_MINUTE_MS;

  const searchParams = useMemo(
    () =>
      getNeighborAlarmsSearchParams(
        startTimestamp,
        endTimestamp,
        [event.cameraId],
        event.location?.id,
        undefined,
        undefined,
        'scan'
      ),
    [event, startTimestamp, endTimestamp]
  );

  const renderContent = useCallback(
    (tableData: TableData) => (
      <div className="pb-6">
        <Table data={tableData} embedded />
      </div>
    ),
    []
  );

  return (
    <BasicPanel title="Neighboring Events">
      {sharedToken ? (
        <SharedNeighboringEvents
          event={event}
          sharedToken={sharedToken}
          startTimestamp={startTimestamp}
          endTimestamp={endTimestamp}
        >
          {renderContent}
        </SharedNeighboringEvents>
      ) : (
        <RegularNeighboringEvents event={event} searchParams={searchParams}>
          {renderContent}
        </RegularNeighboringEvents>
      )}
    </BasicPanel>
  );
}

function getTableData(
  nbrEvents: UnifiedAlarm[],
  currentEvent: UnifiedAlarm,
  onClickRow?: (
    row: UnifiedAlarm,
    e: React.MouseEvent | React.KeyboardEvent
  ) => void
): TableData {
  const allEvents = [...nbrEvents, currentEvent];
  allEvents.sort((a, b) => b.alarmStartTime - a.alarmStartTime);
  const currentIdx = allEvents.findIndex((e) => e.id === currentEvent.id);
  const startSlice = Math.max(0, currentIdx - NEIGHBOR_COUNT);
  const endSlice = Math.min(allEvents.length, currentIdx + NEIGHBOR_COUNT + 1);
  const visibleEvents = allEvents.slice(startSlice, endSlice);
  const highlightedIdx = visibleEvents.findIndex(
    (e) => e.id === currentEvent.id
  );

  return {
    columns: [
      { id: 'alarmType', name: 'Type' },
      { id: 'timestamp', name: 'Time' },
    ],
    rows: visibleEvents.map((curEvent, idx) => ({
      id: `event-${curEvent.id}`,
      highlighted: idx === highlightedIdx,
      onClick: onClickRow ? (e) => onClickRow(curEvent, e) : undefined,
      cells: [
        { value: 'Motion detected' },
        { value: new Date(curEvent.alarmStartTime).toLocaleString() },
      ],
    })),
  };
}

interface RegularProps {
  event: UnifiedAlarm;
  searchParams: string;
  children: (tableData: TableData) => JSX.Element;
}

function RegularNeighboringEvents({
  event,
  searchParams,
  children,
}: RegularProps) {
  const navigate = useNavigate();
  const fetchNeighbourAlarms = useNeighbourAlarms();

  const onClickRow = useCallback(
    (row: UnifiedAlarm, e: React.MouseEvent | React.KeyboardEvent) => {
      const url = `/alarms/${row.id}?product=scan`;
      if (e.ctrlKey || e.metaKey) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(url);
      }
    },
    [navigate]
  );

  const queryResult = useQuery<NeighbourAlarmsResponse, Error>(
    ['neighbor-events', event.id],
    () => fetchNeighbourAlarms(searchParams)
  );

  const alarmsToEvents = useCallback(
    (alarms: UnifiedAlarm[]) =>
      alarms.map(
        (a) => ({ id: a.id, alarmStartTime: a.alarmStartTime } as UnifiedAlarm)
      ),
    []
  );

  return (
    <QueryResult queryResult={queryResult}>
      {(data) => {
        // A window with no neighbours comes back without `alarms` at all.
        const nbrEvents = alarmsToEvents(data.alarms ?? []).filter(
          (a) => a.id !== event.id
        );
        return children(getTableData(nbrEvents, event, onClickRow));
      }}
    </QueryResult>
  );
}

interface SharedProps {
  event: UnifiedAlarm;
  sharedToken: string;
  startTimestamp: number;
  endTimestamp: number;
  children: (tableData: TableData) => JSX.Element;
}

function SharedNeighboringEvents({
  event,
  sharedToken,
  startTimestamp,
  endTimestamp,
  children,
}: SharedProps) {
  const queryResult = useSharedNeighborAlarms(
    sharedToken,
    startTimestamp,
    endTimestamp
  );

  return (
    <QueryResult queryResult={queryResult}>
      {(data) => {
        // A window with no neighbours comes back without `alarms` at all.
        const nbrEvents = (data.alarms ?? []).filter((a) => a.id !== event.id);
        return children(getTableData(nbrEvents, event));
      }}
    </QueryResult>
  );
}

export default NeighboringEvent;
